import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { notificationsApi } from '@/api/notifications'
import { getErrorMessage } from '@/utils/error'
import { useWebPush } from '@/hooks/useWebPush'
import {
  groupNotificationPreferences,
  type NotificationChannel,
  type NotificationPreferences,
} from '@/types/notifications'
import '@/styles/dashboard.css'

const CHANNEL_COLUMNS: NotificationChannel[] = ['database', 'broadcast', 'mail', 'webpush']

export function NotificationPreferencesPage() {
  const { t } = useTranslation()
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const webPush = useWebPush()

  const events = useMemo(
    () => (preferences ? groupNotificationPreferences(preferences.preferences) : []),
    [preferences],
  )

  useEffect(() => {
    setLoading(true)
    notificationsApi
      .getPreferences()
      .then(setPreferences)
      .catch((caught) => setError(getErrorMessage(caught) || t('notifications.loadError')))
      .finally(() => setLoading(false))
  }, [t])

  const reload = () => {
    setLoading(true)
    setError(null)
    notificationsApi
      .getPreferences()
      .then(setPreferences)
      .catch((caught) => setError(getErrorMessage(caught) || t('notifications.loadError')))
      .finally(() => setLoading(false))
  }

  const toggleChannel = (eventKey: string, channel: string, enabled: boolean) => {
    setPreferences((current) =>
      current
        ? {
            ...current,
            preferences: current.preferences.map((row) =>
              row.event_key === eventKey && row.channel === channel
                ? { ...row, enabled }
                : row,
            ),
          }
        : current,
    )
  }

  const save = async () => {
    if (!preferences) return
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      const updated = await notificationsApi.updatePreferences({
        preferences: preferences.preferences
          .filter((row) => row.available)
          .map((row) => ({
            event_key: row.event_key,
            channel: row.channel as NotificationChannel,
            enabled: row.enabled,
          })),
        lesson_start_before_minutes: preferences.settings.lesson_start_before_minutes,
        chat_email_digest_minutes: preferences.settings.chat_email_digest_minutes,
      })
      setPreferences(updated)
      setSuccess(true)
    } catch (caught) {
      setError(getErrorMessage(caught) || t('notifications.preferences.saveError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('notifications.preferences.title')}
          </Typography>
          <PageHelpPopover pageKey="profile-notifications" />
          <Typography color="text.secondary" sx={{ width: 1 }}>
            {t('notifications.preferences.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              !preferences ? (
                <Button color="inherit" size="small" onClick={reload}>
                  {t('notifications.retry')}
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
            {t('notifications.preferences.saved')}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[0, 1, 2].map((item) => (
              <Skeleton key={item} variant="rounded" height={130} />
            ))}
          </Box>
        ) : !preferences ? (
          <Alert severity="info">{t('notifications.loadError')}</Alert>
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('notifications.preferences.browserPush')}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t(`notifications.push.status.${webPush.status}`)}
                </Typography>
                {webPush.error && <Alert severity="error" sx={{ mb: 2 }}>{webPush.error}</Alert>}
                <Button
                  variant={webPush.status === 'enabled' ? 'outlined' : 'contained'}
                  size="small"
                  sx={{ height: 40 }}
                  disabled={
                    webPush.status === 'loading' ||
                    webPush.status === 'unsupported' ||
                    webPush.status === 'denied'
                  }
                  onClick={() =>
                    void (webPush.status === 'enabled' ? webPush.disable() : webPush.enable())
                  }
                >
                  {webPush.status === 'enabled'
                    ? t('notifications.push.disable')
                    : t('notifications.push.enable')}
                </Button>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('notifications.preferences.timing')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    type="number"
                    size="small"
                    label={t('notifications.preferences.lessonLead')}
                    value={preferences.settings.lesson_start_before_minutes}
                    inputProps={{ min: 1 }}
                    onChange={(event) =>
                      setPreferences({
                        ...preferences,
                        settings: {
                          ...preferences.settings,
                          lesson_start_before_minutes: Number(event.target.value),
                        },
                      })
                    }
                  />
                  <TextField
                    type="number"
                    size="small"
                    label={t('notifications.preferences.chatDigest')}
                    value={preferences.settings.chat_email_digest_minutes}
                    inputProps={{ min: 1 }}
                    onChange={(event) =>
                      setPreferences({
                        ...preferences,
                        settings: {
                          ...preferences.settings,
                          chat_email_digest_minutes: Number(event.target.value),
                        },
                      })
                    }
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('notifications.preferences.eventsTitle', 'События')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('notifications.preferences.eventColumn', 'Событие')}</TableCell>
                        {CHANNEL_COLUMNS.map((channelKey) => (
                          <TableCell key={channelKey} align="center">
                            {t(`notifications.channels.${channelKey}`)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow key={event.event_key} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {t(`notifications.events.${event.event_key}`, {
                              defaultValue: event.event_key,
                            })}
                          </TableCell>
                          {CHANNEL_COLUMNS.map((channelKey) => {
                            const channel = event.channels.find(
                              (candidate) => candidate.channel === channelKey,
                            )
                            return (
                              <TableCell key={channelKey} align="center" sx={{ py: 0.25 }}>
                                {channel && (
                                  <Switch
                                    size="small"
                                    checked={channel.enabled}
                                    disabled={!channel.available}
                                    onChange={(change) =>
                                      toggleChannel(
                                        event.event_key,
                                        channel.channel,
                                        change.target.checked,
                                      )
                                    }
                                  />
                                )}
                              </TableCell>
                            )
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Button
              variant="contained"
              size="small"
              sx={{ height: 40 }}
              disabled={saving}
              onClick={() => void save()}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        )}
      </Box>
    </ProfileLayout>
  )
}
